import { classNames } from "@components/libs/classNames";
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import { useCallback, useEffect, useRef, useState } from "react";
import Button, { TextSize } from "../Atoms/Button/Button";
import { ButtonStyle } from "../Atoms/Button/ButtonStyle";

interface EditorButtonsProps {
	confirmButtonText: string;
	onCancel: () => void;
	onConfirm: () => void;
	confirmDisabled?: boolean;
	style?: ButtonStyle;
	className?: string;
}

const EditorButtons = (props: EditorButtonsProps) => {
	const {
		confirmButtonText,
		onCancel,
		onConfirm,
		confirmDisabled = true,
		style = ButtonStyle.orange,
		className,
	} = props;
	const [wasVisible, setWasVisible] = useState(!confirmDisabled);
	const firstRender = useRef(true);

	const handleKeyPress = useCallback(
		(event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !confirmDisabled) {
				event.preventDefault();
				onConfirm();
			}
		},
		[onConfirm, confirmDisabled],
	);

	useEffect(() => {
		if (!confirmDisabled) setWasVisible(true);
		firstRender.current = false;
	}, [confirmDisabled]);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyPress);
		return () => {
			document.removeEventListener("keydown", handleKeyPress);
		};
	}, [handleKeyPress]);

	return (
		<div className={className}>
			<div
				className={classNames("buttons", {
					appear: !confirmDisabled,
					disappear: wasVisible && confirmDisabled && !firstRender.current,
				})}
			>
				<Button
					disabled={confirmDisabled}
					buttonStyle={ButtonStyle.underline}
					textSize={TextSize.S}
					onClick={onCancel}
					isEmUnits={true}
				>
					<span>{t("cancel")}</span>
				</Button>
				<Button
					disabled={confirmDisabled}
					buttonStyle={style}
					textSize={TextSize.S}
					onClick={onConfirm}
					isEmUnits={true}
				>
					<span>{confirmButtonText}</span>
				</Button>
			</div>
		</div>
	);
};

export default styled(EditorButtons)`
	.buttons {
		font-size: 1em;
		gap: 1.2rem;
		display: flex;
		align-items: center;
		flex-direction: row;
		-webkit-box-pack: center;
		justify-content: flex-end;
		transition: all 0.15s forwards;
		opacity: 0;
		height: 0;
	}

	.appear {
		animation: slideDown 0.15s forwards;
	}

	.disappear {
		animation: slideUp 0.15s forwards;
	}

	@keyframes slideDown {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
			height: 2.6em;
		}
	}

	@keyframes slideUp {
		from {
			opacity: 1;
			height: 2.6em;
		}
		to {
			opacity: 0;
			height: 0;
		}
	}
`;
